// Standup history (MUI, /standup/history). Read-only drill-in over closed
// standup days — same data hooks/API calls as the kt StandupHistoryPage; reskinned
// with brainTokens + shared mui/* primitives. List vs day routing via :date param.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import {
  getStandupHistory,
  getStandupBoard,
  type StandupHistoryEntry,
  type StandupBoard,
  type BoardMember,
  type TicketDiff,
  type Focus,
} from "../../lib/api";
import { getStandupTranscript } from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { avatarTint, initials, firstName } from "../dashboard/StandupPage";
import { parseTranscriptDialogue } from "../dashboard/StandupHistoryPage";
import {
  PageHeader,
  SectionCard,
  EmptyState,
  StatusPill,
  MonoLabel,
  Icon,
  CardLabel,
} from "../../mui";

const c = brainTokens.color;

function fmtLongDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-NZ", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatTranscriptForExport(raw: string): string {
  const turns = parseTranscriptDialogue(raw);
  if (!turns.length) return raw;
  return turns.map((t) => `${t.label}: ${t.text}`).join("\n\n");
}

function downloadTranscript(date: string, text: string) {
  const blob = new Blob([formatTranscriptForExport(text)], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `standup-transcript-${date}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function BackLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<Icon name="arrow-left" size={14} stroke={2} />}
      sx={{
        color: c.tealInk,
        fontSize: 13.5,
        fontWeight: 500,
        p: 0,
        mb: 1.75,
        minWidth: 0,
        textTransform: "none",
        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
      }}
    >
      {children}
    </Button>
  );
}

function TranscriptDialogue({ raw }: { raw: string }) {
  const turns = useMemo(() => parseTranscriptDialogue(raw), [raw]);
  if (!turns.length) {
    return (
      <Box
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 13,
          lineHeight: 1.55,
          color: c.ink3,
          whiteSpace: "pre-wrap",
        }}
      >
        {raw}
      </Box>
    );
  }
  const named = turns.filter((t) => t.speakerId !== "_narrator").length;
  if (named === 0) {
    return (
      <Box
        sx={{
          fontFamily: brainTokens.font.mono,
          fontSize: 13,
          lineHeight: 1.55,
          color: c.ink3,
          whiteSpace: "pre-wrap",
        }}
      >
        {raw}
      </Box>
    );
  }
  return (
    <Stack spacing={1.75}>
      {turns.map((t, i) => {
        const tint = avatarTint(t.speakerId);
        return (
          <Box key={`${t.speakerId}-${i}`} sx={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: "10px", alignItems: "start" }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
                bgcolor: tint.bg,
                color: tint.fg,
              }}
              aria-hidden
            >
              {initials(t.label)}
            </Box>
            <Box sx={{ minWidth: 0, pt: "2px" }}>
              <Box sx={{ fontSize: 12.5, fontWeight: 600, color: c.ink, mb: "3px" }}>{t.label}</Box>
              <Box sx={{ fontSize: 14, lineHeight: 1.55, color: c.ink3, whiteSpace: "pre-wrap" }}>{t.text}</Box>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function HistoryList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<StandupHistoryEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    getStandupHistory(ctrl.signal)
      .then(setRows)
      .catch((e) => {
        if (!ctrl.signal.aborted) setErr(e instanceof Error ? e.message : "Failed to load history.");
      });
    return () => ctrl.abort();
  }, []);

  return (
    <Box>
      <BackLink onClick={() => navigate("/standup")}>Back to today&apos;s standup</BackLink>
      <PageHeader
        title="Standup history"
        eyebrow="Past standups, newest first. Open a day to review the board, recaps and ticket movement."
      />

      {!rows && !err && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      )}
      {err && (
        <Box sx={{ color: c.dangerText, fontSize: 14, mt: 2.25 }}>{err}</Box>
      )}
      {rows && rows.length === 0 && (
        <Box sx={{ mt: 3 }}>
          <EmptyState title="No past standups yet." />
        </Box>
      )}
      {rows && rows.length > 0 && (
        <Stack spacing="10px" sx={{ mt: 3 }}>
          {rows.map((r) => (
            <Box
              key={r.date}
              component="button"
              onClick={() => navigate(`/standup/history/${r.date}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                p: "16px 18px",
                borderRadius: `${brainTokens.radius.card}px`,
                bgcolor: c.white,
                border: `1px solid ${c.border}`,
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                fontFamily: "inherit",
                transition: "all .12s ease",
                "&:hover": { borderColor: c.teal, boxShadow: brainTokens.shadow.card },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 14,
                  fontWeight: 600,
                  color: c.ink,
                  flex: "none",
                  minWidth: 122,
                }}
              >
                {r.date}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 13.5,
                  color: r.teamDigest ? c.ink3 : c.faint,
                  fontStyle: r.teamDigest ? "normal" : "italic",
                  lineHeight: 1.5,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {r.teamDigest || "No team digest captured."}
              </Box>
              <Box component="span" sx={{ color: c.faint2, fontSize: 16, flex: "none" }}>
                ›
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function YesterdayList({ focuses, recap }: { focuses: Focus[]; recap?: string }) {
  if (!recap && focuses.length === 0) {
    return (
      <Box sx={{ fontSize: 14, color: c.muted, py: "7px", px: "4px" }}>Nothing logged.</Box>
    );
  }
  return (
    <>
      {recap && (
        <Box sx={{ fontSize: 13.5, lineHeight: 1.5, color: c.ink4, mb: 1.5 }}>{recap}</Box>
      )}
      <Stack spacing="6px">
        {focuses.map((f) => (
          <Box key={f.id} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "4px",
                border: `1.5px solid ${f.done ? c.successBorder : c.border}`,
                bgcolor: f.done ? c.successBg : c.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                color: c.successText,
                flex: "none",
              }}
            >
              {f.done && "✓"}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 14,
                flex: 1,
                color: f.done ? c.faint : c.ink2,
                textDecoration: f.done ? "line-through" : "none",
              }}
            >
              {f.text}
            </Box>
            {f.ticketKey && (
              <Box
                component="span"
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 11,
                  px: "7px",
                  py: "2px",
                  borderRadius: "4px",
                  bgcolor: c.panelBg2,
                  color: c.muted,
                  flex: "none",
                }}
              >
                {f.ticketKey}
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </>
  );
}

function TicketDiffList({ diff }: { diff: TicketDiff[] }) {
  if (diff.length === 0) {
    return <Box sx={{ fontSize: 13, color: c.muted2 }}>No ticket movement recorded.</Box>;
  }
  return (
    <Stack spacing="8px">
      {diff.map((d) => {
        const rowStyle = d.movedToDone
          ? { bgcolor: c.panelBg, borderColor: c.borderLight }
          : d.newlyBlocked
            ? { bgcolor: c.dangerBg, borderColor: c.dangerBorder }
            : d.statusChanged
              ? { bgcolor: c.warnBg3, borderColor: c.warnBorder }
              : { bgcolor: c.white, borderColor: c.borderLight };
        return (
          <Box
            key={d.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "11px",
              p: "10px 13px",
              borderRadius: "7px",
              border: `1px solid ${rowStyle.borderColor}`,
              bgcolor: rowStyle.bgcolor,
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 12,
                px: "8px",
                py: "2px",
                borderRadius: "4px",
                flex: "none",
                bgcolor: c.panelBg2,
                color: c.ink4,
              }}
            >
              {d.key}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 14,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: c.ink3,
              }}
            >
              {d.summary}
            </Box>
            {d.movedToDone && (
              <StatusPill tone="success" label="Moved to done" sx={{ fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: brainTokens.font.mono }} />
            )}
            {d.newlyBlocked && (
              <StatusPill tone="danger" label="Newly blocked" sx={{ fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: brainTokens.font.mono }} />
            )}
            {d.statusChanged && !d.movedToDone && !d.newlyBlocked && (
              <>
                <StatusPill tone="neutral" label="Status change" sx={{ fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: brainTokens.font.mono }} />
                {(d.fromStatus || d.toStatus) && (
                  <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint, flex: "none" }}>
                    {d.fromStatus ?? "—"} → {d.toStatus ?? "—"}
                  </Box>
                )}
              </>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function ReadOnlyFocusList({ focuses }: { focuses: Focus[] }) {
  if (focuses.length === 0) {
    return <Box sx={{ fontSize: 14, color: c.muted, py: "7px", px: "4px" }}>No focuses logged.</Box>;
  }
  return (
    <Stack spacing="1px">
      {focuses.map((f) => (
        <Box key={f.id} sx={{ display: "flex", alignItems: "center", gap: "12px", py: "5px", px: "8px", borderRadius: "7px" }}>
          <Box
            role="checkbox"
            aria-checked={f.done}
            aria-disabled
            sx={{
              width: 20,
              height: 20,
              borderRadius: "5px",
              border: `1.5px solid ${f.done ? c.successBorder : c.border}`,
              bgcolor: f.done ? c.successBg : c.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: c.successText,
              flex: "none",
              opacity: 0.85,
            }}
          >
            {f.done && "✓"}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 14.5,
              flex: 1,
              color: f.done ? c.faint : c.ink2,
              textDecoration: f.done ? "line-through" : "none",
            }}
          >
            {f.text}
          </Box>
          {f.ticketKey && (
            <Box
              component="span"
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 11,
                px: "7px",
                py: "2px",
                borderRadius: "4px",
                bgcolor: c.panelBg2,
                color: c.muted,
                flex: "none",
              }}
            >
              {f.ticketKey}
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
}

function HistoricalPersonTab({ member }: { member: BoardMember }) {
  const focuses = member.focuses ?? [];
  const doneCount = focuses.filter((f) => f.done).length;
  const y = member.yesterday;
  const diff = member.ticketDiff ?? [];

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "18px" }}>
        <SectionCard
          title={
            <Box>
              <CardLabel>That day</CardLabel>
              <Box sx={{ fontSize: 15, fontWeight: 600, color: c.ink, mt: "4px" }}>Focuses</Box>
            </Box>
          }
          actions={
            <MonoLabel color={c.muted}>
              {doneCount}/{focuses.length}
            </MonoLabel>
          }
        >
          <ReadOnlyFocusList focuses={focuses} />
        </SectionCard>

        <SectionCard
          sx={{ bgcolor: c.panelBg3 }}
          title={
            <Box>
              <CardLabel>Recap</CardLabel>
              <Box sx={{ fontSize: 15, fontWeight: 600, color: c.ink, mt: "4px" }}>End-of-day summary</Box>
            </Box>
          }
        >
          <YesterdayList focuses={y?.focuses ?? []} recap={member.recap ?? y?.recap} />
        </SectionCard>
      </Box>

      <SectionCard title="Ticket movement" actions={<MonoLabel>JIRA</MonoLabel>} sx={{ mt: "18px" }}>
        <TicketDiffList diff={diff} />
      </SectionCard>
    </Box>
  );
}

function HistoryDay({ date }: { date: string }) {
  const navigate = useNavigate();
  const [board, setBoard] = useState<StandupBoard | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<string>("");

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setErr(null);
      getStandupBoard(date, signal)
        .then((b) => {
          setBoard(b);
          const first = (b.members ?? []).filter((m) => m.kind !== "brain")[0];
          setActive(first?.userId ?? "");
        })
        .catch((e) => {
          if (!signal?.aborted) setErr(e instanceof Error ? e.message : "Failed to load standup.");
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
      getStandupTranscript(date)
        .then((r) => setTranscript(r?.transcript ?? null))
        .catch(() => setTranscript(null));
    },
    [date],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const people = useMemo(() => (board?.members ?? []).filter((m) => m.kind !== "brain"), [board]);
  const activeMember = people.find((m) => m.userId === active);

  return (
    <Box>
      <BackLink onClick={() => navigate("/standup/history")}>Back to history</BackLink>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 3,
          pb: 2.5,
          mb: 2.75,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <Box>
          <Box
            sx={{
              fontFamily: brainTokens.font.mono,
              fontSize: 34,
              fontWeight: 500,
              color: c.ink,
              letterSpacing: "-0.01em",
              lineHeight: 1,
            }}
          >
            {date}
          </Box>
          <Box sx={{ fontSize: 13.5, color: c.muted2, mt: 1 }}>{fmtLongDate(date)}</Box>
        </Box>
        <StatusPill tone="neutral" label="Closed — read-only" dot />
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      )}
      {err && !loading && <Box sx={{ color: c.dangerText, fontSize: 14 }}>{err}</Box>}

      {!loading && !err && board && (
        <>
          <SectionCard
            title={
              <Box>
                <CardLabel>Team digest</CardLabel>
                <Box sx={{ fontSize: 15, fontWeight: 600, color: c.ink, mt: "4px" }}>What the team did</Box>
              </Box>
            }
            sx={{ mb: "18px" }}
          >
            <Box
              sx={{
                fontSize: 14,
                lineHeight: 1.6,
                color: board.teamDigest ? c.ink3 : c.faint,
                fontStyle: board.teamDigest ? "normal" : "italic",
                whiteSpace: "pre-wrap",
              }}
            >
              {board.teamDigest || "No team digest captured for this day."}
            </Box>
          </SectionCard>

          <SectionCard
            sx={{ bgcolor: c.panelBg3, mb: "18px" }}
            title={
              <Box>
                <CardLabel>Transcript</CardLabel>
                <Box sx={{ fontSize: 15, fontWeight: 600, color: c.ink, mt: "4px" }}>Standup discussion</Box>
              </Box>
            }
            actions={
              transcript ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => downloadTranscript(date, transcript)}
                  startIcon={<Icon name="download" size={14} stroke={2} />}
                >
                  Export .txt
                </Button>
              ) : undefined
            }
          >
            <Box sx={{ mt: transcript ? 0 : 0, maxHeight: 520, overflow: "auto" }}>
              {transcript ? (
                <TranscriptDialogue raw={transcript} />
              ) : (
                <Box sx={{ fontSize: 14, lineHeight: 1.6, color: c.faint, fontStyle: "italic" }}>
                  No transcript available for this day.
                </Box>
              )}
            </Box>
          </SectionCard>

          {people.length === 0 ? (
            <EmptyState title="No one was on the board this day." />
          ) : (
            <>
              <Tabs
                value={active}
                onChange={(_, v: string) => setActive(v)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="People"
                sx={{ mb: 2 }}
              >
                {people.map((m) => {
                  const tint = avatarTint(m.userId);
                  return (
                    <Tab
                      key={m.userId}
                      value={m.userId}
                      aria-label={m.displayName}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: tint.bg,
                              color: tint.fg,
                            }}
                          >
                            {initials(m.displayName)}
                          </Box>
                          {firstName(m.displayName)}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
              {activeMember && <HistoricalPersonTab member={activeMember} />}
            </>
          )}
        </>
      )}
    </Box>
  );
}

export function StandupHistoryPageMui() {
  const { date } = useParams<{ date?: string }>();
  return date ? <HistoryDay date={date} /> : <HistoryList />;
}
